import { useMutation, useQuery, useQueryClient } from 'react-query';
import type { ConnectorIntegrationMeta } from '@bandido/shared/types';

import { client } from '@frontend/features/common/services';
import { QueryKeys } from '@frontend/features/common/types';

import { connectorCapabilitiesQueryKey } from './connectorCapabilitiesQueryKey';
import { ownershipCatalogQueryPrefix } from './ownershipCatalogAuthority';

export interface RuteroConnectorField {
  name: string;
  type: 'text' | 'url' | 'password';
  required: boolean;
}

/** Estado de un conector del catálogo de Rutero para la agencia. Solo metadata: las credenciales son write-only. */
export interface RuteroConnectorStatus {
  integration?: ConnectorIntegrationMeta;
  connector: string;
  provider: string;
  vertical: string;
  fields: RuteroConnectorField[];
  configured: boolean;
  enabled: boolean;
  updatedAt?: string;
}

export interface UpsertRuteroConnectorPayload {
  integration?: Pick<ConnectorIntegrationMeta, 'name' | 'environment'> & { webhookSecret?: string };
  connector: string;
  /** Autoridad de cache frontend; no forma parte del DTO HTTP. */
  vertical: string;
  credentials: Record<string, string>;
  enabled?: boolean;
}

export interface DeleteRuteroConnectorPayload {
  connector: string;
  /** Autoridad de cache frontend; no forma parte del DTO HTTP. */
  vertical: string;
}

function invalidateConnectorAuthorities(
  queryClient: ReturnType<typeof useQueryClient>,
  agencyId: number,
  vertical: string,
): void {
  queryClient.invalidateQueries({
    queryKey: [QueryKeys.getRuteroConnectors, agencyId],
  });
  queryClient.invalidateQueries({
    queryKey: connectorCapabilitiesQueryKey(agencyId),
  });
  queryClient.invalidateQueries({
    queryKey: ownershipCatalogQueryPrefix(agencyId, vertical),
  });
}

export const useRuteroConnectors = (agencyId: number) =>
  useQuery({
    queryKey: [QueryKeys.getRuteroConnectors, agencyId],
    queryFn: async (): Promise<RuteroConnectorStatus[]> => {
      const response = await client.get<RuteroConnectorStatus[]>(`agencies/${agencyId}/rutero/connector-accounts`);
      return response.data;
    },
    enabled: Boolean(agencyId),
    refetchOnWindowFocus: true,
  });

export const useUpsertRuteroConnector = (agencyId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ connector, credentials, enabled, integration }: UpsertRuteroConnectorPayload) => {
      const response = await client.put(`agencies/${agencyId}/rutero/connector-accounts/${connector}`, {
        credentials,
        enabled,
        ...(integration ? { integration } : {}),
      });
      return response.data;
    },
    onSuccess: (_data, variables) => {
      invalidateConnectorAuthorities(queryClient, agencyId, variables.vertical);
    },
  });
};

export const useDeleteRuteroConnector = (agencyId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ connector }: DeleteRuteroConnectorPayload) => {
      await client.delete(`agencies/${agencyId}/rutero/connector-accounts/${connector}`);
    },
    onSuccess: (_data, variables) => {
      invalidateConnectorAuthorities(queryClient, agencyId, variables.vertical);
    },
  });
};
